import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import {
  Button,
  CodeLabel,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  InputText,
  ItemPicker,
  Modal,
  Select,
  Spinner,
  Table,
  Tag,
} from '../../components'
import type { PickedItem, TableColumn } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { trpcMessage } from '../../lib/trpcMessages'
import { MIRROR_REFETCH_MS, trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import {
  AdminPageHeader,
  ReadOnlyNote,
  adminStyles as styles,
  changedFields,
  formatGrams,
  hasFields,
  numberText,
  parseNonNegative,
} from '../admin/AdminPage'
import { MACHINE_FAMILY_OPTIONS, isMachineFamily } from './planning'
import type { MachineFamily } from './planning'
import { useConfigurationRefresh } from './useConfigurationRefresh'

type QualityRule = RouterOutputs['configuration']['listQualityRules']['rules'][number]

type RecipeRules = {
  recipe: QualityRule['recipe']
  rules: Array<QualityRule>
}

const MIN_ABOVE_MAX = 'El peso mínimo no puede ser mayor que la capacidad máxima.'

/** The rules of each recipe, in the order the API lists them: position order, so a recipe's first rule is its default. */
const byRecipe = (rules: Array<QualityRule>): Array<RecipeRules> => {
  const groups = new Map<string, RecipeRules>()
  for (const rule of rules) {
    const group = groups.get(rule.recipeCode)
    if (group === undefined) {
      groups.set(rule.recipeCode, { recipe: rule.recipe, rules: [rule] })
    } else {
      group.rules.push(rule)
    }
  }
  return [...groups.values()]
}

/**
 * CALIDAD: for each recipe, the pot families it may be cooked in, with the
 * minimum weight that justifies each and an optional capacity override. A
 * recipe's rules apply in order and the first is its default, so the order is
 * edited here too — within the recipe, never across recipes.
 */
export const QualityRulesPage = (): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const refreshStatus = useConfigurationRefresh()

  const [creatingFor, setCreatingFor] = useState<PickedItem | null | undefined>(undefined)
  const [editing, setEditing] = useState<QualityRule | null>(null)
  const [deleting, setDeleting] = useState<QualityRule | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const rulesQuery = trpc.configuration.listQualityRules.useQuery(undefined, { refetchInterval: MIRROR_REFETCH_MS })

  const afterWrite = (): void => {
    setActionError(null)
    void utils.configuration.listQualityRules.invalidate()
    refreshStatus()
  }

  const move = trpc.configuration.moveQualityRule.useMutation({
    onSuccess: afterWrite,
    onError: (error) => setActionError(trpcMessage(error)),
  })
  const remove = trpc.configuration.deleteQualityRule.useMutation({
    onSuccess: () => {
      setDeleting(null)
      afterWrite()
    },
    onError: (error) => {
      setDeleting(null)
      setActionError(trpcMessage(error))
    },
  })

  const columnsFor = (rules: Array<QualityRule>): Array<TableColumn<QualityRule>> => [
    {
      key: 'family',
      header: 'Familia',
      render: (rule) => (
        <span className={styles['admin-page__tags']}>
          <strong>{rule.machineFamily}</strong>
          {rules.indexOf(rule) === 0 && <Tag tone="info" label="por defecto" />}
        </span>
      ),
    },
    {
      key: 'min',
      header: 'Peso mínimo',
      align: 'right',
      render: (rule) => <span className={styles['admin-page__number']}>{formatGrams(rule.minWeight, 'Sin mínimo')}</span>,
    },
    {
      key: 'max',
      header: 'Capacidad máxima',
      align: 'right',
      render: (rule) => <span className={styles['admin-page__number']}>{formatGrams(rule.maxCapacity, 'La de la máquina')}</span>,
    },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (rule) => {
        const index = rules.indexOf(rule)
        const name = `${rule.machineFamily} de ${rule.recipeCode}`
        return (
          <div className={styles['admin-page__row-actions']}>
            <Button
              variant="secondary"
              disabled={isReadOnly || move.isPending || index === 0}
              onClick={() => move.mutate({ id: rule.id, direction: 'up' })}
              aria-label={`Subir ${name}`}
              label="Subir"
            />
            <Button
              variant="secondary"
              disabled={isReadOnly || move.isPending || index === rules.length - 1}
              onClick={() => move.mutate({ id: rule.id, direction: 'down' })}
              aria-label={`Bajar ${name}`}
              label="Bajar"
            />
            <Button
              variant="secondary"
              disabled={isReadOnly}
              onClick={() => setEditing(rule)}
              aria-label={`Editar ${name}`}
              label="Editar"
            />
            <Button
              variant="danger"
              disabled={isReadOnly}
              onClick={() => setDeleting(rule)}
              aria-label={`Eliminar ${name}`}
              label="Eliminar"
            />
          </div>
        )
      },
    },
  ]

  let body: ReactElement
  if (rulesQuery.isPending) {
    body = <Spinner label="Cargando reglas de calidad…" />
  } else if (rulesQuery.isError) {
    body = <ErrorState error={rulesQuery.error} onRetry={() => void rulesQuery.refetch()} />
  } else if (rulesQuery.data.rules.length === 0) {
    body = (
      <EmptyState
        title="No hay reglas de calidad"
        message="Sin ellas el planificador no puede generar un plan."
        action={<Button disabled={isReadOnly} onClick={() => setCreatingFor(null)} label="Añadir la primera" />}
      />
    )
  } else {
    body = (
      <>
        <FreshnessBadge freshness={rulesQuery.data.freshness} />
        {byRecipe(rulesQuery.data.rules).map(({ recipe, rules }) => (
          <section key={recipe.code} className={styles['admin-page__section']}>
            <div className={styles['admin-page__section-heading']}>
              <h2 className={styles['admin-page__section-title']}>
                <CodeLabel code={recipe.code} title={recipe.title} known={recipe.known} />
              </h2>
              <Button
                variant="secondary"
                disabled={isReadOnly}
                onClick={() => setCreatingFor({
                  code: recipe.code,
                  title: recipe.title,
                  groupTitle: recipe.groupTitle,
                  known: recipe.known,
                })}
                aria-label={`Añadir regla a ${recipe.code}`}
                label="Añadir regla"
              />
            </div>
            <Table columns={columnsFor(rules)} rows={rules} rowKey={(rule) => rule.id} caption={`Reglas de ${recipe.code}`} />
          </section>
        ))}
      </>
    )
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Reglas de calidad"
        intro="En qué familias de olla se puede cocinar cada receta, con el peso mínimo que lo justifica. La primera regla de cada receta es la que se usa por defecto."
        actions={<Button disabled={isReadOnly} onClick={() => setCreatingFor(null)} label="Nueva regla" />}
      />
      {isReadOnly && <ReadOnlyNote />}
      {actionError !== null && <p role="alert" className={styles['admin-page__error']}>{actionError}</p>}

      {body}

      {creatingFor !== undefined && (
        <QualityRuleDialog
          initialRecipe={creatingFor}
          onClose={() => setCreatingFor(undefined)}
          onSaved={() => {
            setCreatingFor(undefined)
            afterWrite()
          }}
        />
      )}
      {editing !== null && (
        <QualityRuleDialog
          rule={editing}
          initialRecipe={{
            code: editing.recipe.code,
            title: editing.recipe.title,
            groupTitle: editing.recipe.groupTitle,
            known: editing.recipe.known,
          }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            afterWrite()
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        variant="danger"
        title="Eliminar regla de calidad"
        message={deleting === null
          ? ''
          : `Se eliminará la regla ${deleting.machineFamily} de ${deleting.recipeCode}. Si era la primera, la siguiente pasa a ser la de por defecto.`}
        confirmLabel="Eliminar"
        isLoading={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting !== null) {
            remove.mutate({ id: deleting.id })
          }
        }}
      />
    </section>
  )
}

type QualityRuleDialogProps = {
  /** The rule being edited; absent to create one (it goes last among its recipe's rules). */
  rule?: QualityRule
  initialRecipe: PickedItem | null
  onClose: () => void
  onSaved: () => void
}

const QualityRuleDialog = ({ rule, initialRecipe, onClose, onSaved }: QualityRuleDialogProps): ReactElement => {
  const [recipe, setRecipe] = useState<PickedItem | null>(initialRecipe)
  const [family, setFamily] = useState<MachineFamily | ''>(rule?.machineFamily ?? '')
  const [minWeight, setMinWeight] = useState(numberText(rule?.minWeight ?? null))
  const [maxCapacity, setMaxCapacity] = useState(numberText(rule?.maxCapacity ?? null))
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onError = (error: unknown): void => setErrorMessage(trpcMessage(error))
  const create = trpc.configuration.createQualityRule.useMutation({ onSuccess: onSaved, onError })
  const update = trpc.configuration.updateQualityRule.useMutation({ onSuccess: onSaved, onError })
  const isPending = create.isPending || update.isPending

  const parsedMin = parseNonNegative(minWeight)
  const parsedMax = parseNonNegative(maxCapacity)
  const min = parsedMin.valid ? parsedMin.value : null
  const max = parsedMax.valid ? parsedMax.value : null
  const numbersValid = parsedMin.valid && parsedMax.valid
  // The API's own rule (`minWeightAboveCapacity`), checked before the round-trip.
  const minAboveMax = min !== null && max !== null && min > max

  const draft = recipe !== null && family !== '' && numbersValid && !minAboveMax
    ? { recipeCode: recipe.code, machineFamily: family, minWeight: min, maxCapacity: max }
    : null
  const patch = draft === null || rule === undefined
    ? {}
    : changedFields({
      recipeCode: rule.recipeCode,
      machineFamily: rule.machineFamily,
      minWeight: rule.minWeight,
      maxCapacity: rule.maxCapacity,
    }, draft)
  const canSubmit = draft !== null && (rule === undefined || hasFields(patch))

  return (
    <Modal open title={rule === undefined ? 'Nueva regla de calidad' : 'Editar regla de calidad'} onClose={onClose} maxWidth="56rem">
      <form
        className={styles['admin-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          if (draft === null) {
            return
          }
          setErrorMessage(null)
          if (rule === undefined) {
            create.mutate(draft)
          } else {
            update.mutate({ id: rule.id, ...patch })
          }
        }}
      >
        <ItemPicker label="Receta" selected={recipe} onSelect={setRecipe} />
        <Select
          label="Familia"
          placeholder="Elige una familia…"
          options={MACHINE_FAMILY_OPTIONS}
          value={family}
          onChange={(event) => {
            const { value } = event.target
            setFamily(isMachineFamily(value) ? value : '')
          }}
        />
        <InputText
          label="Peso mínimo (g)"
          inputMode="decimal"
          value={minWeight}
          hint="Vacío = sin mínimo."
          onChange={(event) => setMinWeight(event.target.value)}
        />
        <InputText
          label="Capacidad máxima (g)"
          inputMode="decimal"
          value={maxCapacity}
          hint="Vacío = la capacidad de la máquina."
          onChange={(event) => setMaxCapacity(event.target.value)}
        />
        {!numbersValid && <p role="alert" className={styles['admin-page__error']}>Los pesos son gramos, 0 o más.</p>}
        {minAboveMax && <p role="alert" className={styles['admin-page__error']}>{MIN_ABOVE_MAX}</p>}
        {errorMessage !== null && <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p>}
        <div className={styles['admin-page__form-actions']}>
          <Button variant="secondary" onClick={onClose} disabled={isPending} label="Cancelar" />
          <Button
            type="submit"
            disabled={isPending || !canSubmit}
            label={rule === undefined ? 'Crear regla' : 'Guardar cambios'}
          />
        </div>
      </form>
    </Modal>
  )
}
